import { useMemo } from 'react'
import groupBy from 'lodash/groupBy'
import mapValues from 'lodash/mapValues'
import flatMap from 'lodash/flatMap'
import uniqBy from 'lodash/uniqBy'

import { initialActiveTabLabel } from '../../candidates/CandidatesContainer'

import { useQuery } from '@percent/partner-dashboard/common/hooks'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import {
  CurrentStateDB,
  ProgramStateOption,
  TaskTemplate
} from '@percent/partner-dashboard/services/grants/grantsService.types'

type UseProgramStateConfig = {
  programId: string
  filterNullvalues?: boolean
  enabled?: boolean
}

export const useProgramStates = ({ programId, filterNullvalues = true, enabled = true }: UseProgramStateConfig) => {
  const { grantsService } = useServices()

  const [{ data, isLoading, error }, { refresh }] = useQuery(
    grantsService.getProgramStates,
    { programId },
    enabled && !!programId
  )

  const states = data?.states ?? []

  const filteredStates = filterNullvalues
    ? states.filter(s => (!!s.displayGroup || !!s.displayKey) && s.displayMode !== 'hidden')
    : states

  const memo = useMemo(() => {
    // Individual states for filters
    const allStateOptions: ProgramStateOption[] = filteredStates.map(s => ({
      label: s.displayKey ?? s.displayGroup ?? 'Unknown',
      key: s.id,
      disabled: s.displayMode === 'disabled',
      tableType: s.tableType ?? null,
      scoreName: s.scoreName ?? undefined
    }))

    // Group by displayGroup if available, otherwise by displayKey
    const grouped = groupBy(filteredStates, s => s.displayGroup ?? s.displayKey ?? 'Unknown')

    // Create state groups lookup (group name -> state IDs)
    const groupsByName = mapValues(grouped, groupStates => groupStates.map(s => s.id))

    // Create task templates lookup (group name -> all templates)
    const templatesByName = mapValues(grouped, groupStates =>
      uniqBy(
        flatMap(groupStates, s => s.taskTemplates || []),
        'id'
      )
    )

    // Create grouped states for tabs
    const groupedOptions: ProgramStateOption[] = Object.entries(grouped).map(([groupName, groupStates]) => ({
      label: groupName,
      key: groupName,
      disabled: groupStates.some(s => s.displayMode === 'disabled'),
      tableType: groupStates[0]?.tableType ?? null,
      scoreName: groupStates[0]?.scoreName ?? undefined
    }))

    return {
      groupedStates: groupedOptions,
      allStates: allStateOptions,
      stateGroups: groupsByName,
      taskTemplates: templatesByName
    }
  }, [filteredStates])

  const { groupedStates, allStates, stateGroups, taskTemplates } = memo

  const tabs = useMemo(
    () => [
      {
        label: initialActiveTabLabel,
        key: CurrentStateDB.ALL,
        disabled: false,
        tableType: null
      },
      ...groupedStates
    ],
    [groupedStates]
  )

  const getStateIdsForGroup = (groupKey: string): string[] => stateGroups[groupKey] || []
  const getTaskTemplatesForGroup = (groupKey: string): TaskTemplate[] => taskTemplates[groupKey] || []

  return {
    unmappedStates: states,
    groupedStates,
    allStates,
    tabs,
    taskTemplates,
    getStateIdsForGroup,
    getTaskTemplatesForGroup,
    isLoading,
    error,
    refetch: refresh
  }
}
